/**
 * Product card swatch: seleção de variantes, preço e imagem no card.
 *
 * Modificações em relação ao tema base:
 * - keepFeaturedImage (data-keep-featured-image="true"): mantém a imagem principal fixa;
 *   não atualiza opções/imagem ao carregar nem ao trocar variante (útil em listagens).
 * - Sale badge: removido deste componente (seletor saleBadge e updateSaleBadge);
 *   selo de promoção não é mais gerenciado aqui.
 * - Foto da cor (ONC-173): nenhuma variante da loja tem imagem nativa, então a foto
 *   de cada cor vem do agrupamento de imagens por variante do FoxKit — a mesma fonte
 *   que a PDP usa. Ver getVariantGroupImages e product-card-option.liquid.
 * - Desconto Pix: NÃO se aplica ao card (ONC-148) — a vitrine mostra o preço
 *   normal; a informação de economia no Pix vive só na página do produto.
 */
if (!customElements.get("pcard-swatch")) {
  // Cards do mesmo produto compartilham uma unica requisicao de
  // /products/{handle}.js. A home repetia 184 requisicoes para 60 produtos
  // distintos, porque cada card pedia o seu, e o Swiper ainda clona os slides.
  const productJsonRequests = new Map();

  // O card so busca os dados quando chega perto da viewport, em vez de todos
  // dispararem no carregamento competindo com a imagem do hero e segurando a
  // thread principal. Sem IntersectionObserver, mantem o comportamento antigo.
  // A margem horizontal existe porque a maior parte dos cards vive em carrossel:
  // os vizinhos ficam prontos antes de o cliente deslizar ate eles.
  const NEAR_VIEWPORT_MARGIN = "1000px 800px";
  let nearViewportObserver = null;

  // A URL do CDN ja vem com ?v= e, quando sai do Liquid, com &width=. Trocar a
  // largura no lugar de concatenar evita URL com dois width.
  function withImageWidth(src, width) {
    if (!src) return src;
    if (/[?&]width=\d+/.test(src)) return src.replace(/width=\d+/g, `width=${width}`);
    return `${src}${src.includes("?") ? "&" : "?"}width=${width}`;
  }

  // src e srcset andam juntos: trocar so o src deixaria o navegador escolher uma
  // largura do srcset antigo, que ainda e a foto da cor anterior. As larguras sao
  // as mesmas que o product-card-9.js usa, para as duas trocas renderem igual.
  const CARD_IMAGE_WIDTHS = [165, 360, 535, 750, 940, 1100];

  function setCardImage(wrapper, src) {
    const img = wrapper && wrapper.querySelector("img");
    if (!img || !src) return;

    const nextSrc = withImageWidth(src, 1100);
    if (img.getAttribute("src") === nextSrc) return;

    img.setAttribute("srcset", CARD_IMAGE_WIDTHS.map((w) => `${withImageWidth(src, w)} ${w}w`).join(", "));
    img.setAttribute("src", nextSrc);

    // O responsive-image do tema guarda a URL do Liquid no construtor e a reaplica
    // quando o card entra na viewport (loadImage). Sem atualizar essa copia, a foto
    // volta para a cor anterior em todo card que carrega preguicoso.
    const responsiveImage = wrapper.querySelector("responsive-image");
    if (responsiveImage) responsiveImage.src = nextSrc;
  }

  // Cor de uma foto pelo nome do arquivo, para produto sem agrupamento do FoxKit.
  // Serve so para escolher a bolinha que abre marcada, nunca para trocar a foto:
  // os nomes sao irregulares (WhatsApp..., Design_sem_nome...), entao quando nao
  // da para saber, o card fica como estava. Roupas trazem o codigo de cor do SKU
  // (01VE81919-020001-PP -> 01VE81919_020001_02.jpg); outras trazem o nome
  // (02CF90577_CINZA_01.jpg).
  function normalizeText(value) {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();
  }

  // Codigo curto demais ("01", "00001") colide com numero de foto; fica de fora.
  function significantCode(code) {
    const stripped = String(code || "").replace(/^0+/, "");
    return /^\d+$/.test(code) && stripped.length >= 3 ? stripped : null;
  }

  function colorCodesByValue(variants, position) {
    const codes = {};
    variants.forEach((variant) => {
      const code = significantCode(String(variant.sku || "").split("-")[1]);
      if (!code) return;
      const color = variant.options[position];
      (codes[color] = codes[color] || new Set()).add(code);
    });
    return codes;
  }

  function colorFromFileName(src, colors, codes) {
    const file = String(src || "").split("?")[0].split("/").pop();
    const tokens = normalizeText(file).replace(/\.[A-Z0-9]+$/, "").split(/[_\-.\s]+/);
    const hits = new Set();

    tokens.forEach((token) => {
      const code = significantCode(token);
      if (!code) return;
      colors.forEach((color) => {
        if (codes[color] && codes[color].has(code)) hits.add(color);
      });
    });

    // Nome da cor como sequencia de palavras: VERDE OLIVA casa com _VERDE_OLIVA_.
    const joined = `_${tokens.join("_")}_`;
    const byName = colors
      .filter((color) => joined.includes(`_${normalizeText(color).split(/\s+/).join("_")}_`))
      .sort((a, b) => b.length - a.length);
    if (byName.length && !byName.some((color) => !byName[0].includes(color))) hits.add(byName[0]);

    return hits.size === 1 ? Array.from(hits)[0] : null;
  }

  // Sem os dados do produto o card fica morto: as bolinhas nem respondem ao clique.
  // No preview pelo dominio myshopify a requisicao ainda passa por um redirecionamento
  // para o dominio principal, que falha de vez em quando; duas novas tentativas,
  // espacadas, cobrem essas falhas passageiras.
  function fetchJsonWithRetry(url, attemptsLeft = 3) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        if (attemptsLeft <= 1) throw error;
        const delay = attemptsLeft === 3 ? 800 : 2000;
        return new Promise((resolve) => setTimeout(resolve, delay)).then(() =>
          fetchJsonWithRetry(url, attemptsLeft - 1)
        );
      });
  }

  function whenNearViewport(element) {
    if (!element || !("IntersectionObserver" in window)) return Promise.resolve();

    if (!nearViewportObserver) {
      nearViewportObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const resolve = entry.target.nearViewportResolve;
            if (!resolve) return;
            delete entry.target.nearViewportResolve;
            nearViewportObserver.unobserve(entry.target);
            resolve();
          });
        },
        { rootMargin: NEAR_VIEWPORT_MARGIN }
      );
    }

    return new Promise((resolve) => {
      element.nearViewportResolve = resolve;
      nearViewportObserver.observe(element);
    });
  }

  class ProductCardColorSwatch extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        container: "[data-pcard-variant-picker]",
        optionNodes: [".m-product-option--node__label"],
        featuredImage: ".m-product-card__main-image",
        pcard: ".m-product-card",
        variantDropdown: ".m-product-option--dropdown-select",
        priceWrapper: ".m-price",
        salePrice: ".m-price-item--sale",
        compareAtPrice: [".m-price-item--regular"],
        unitPrice: ".m-price__unit",
        soldOutBadge: ".m-product-tag--soldout",
      };
      this.container = this.closest(this.selectors.container);
      this.pcard = this.closest(this.selectors.pcard);
      this.variantIdNode = this.pcard && this.pcard.querySelector('[name="id"]');
      this.featuredImage = this.pcard && this.pcard.querySelector(this.selectors.featuredImage);
      this.keepFeaturedImage = this.dataset.keepFeaturedImage === "true";
      this.domNodes = queryDomNodes(this.selectors, this.pcard);
      this.initializeData();
    }

    async initializeData() {
      this.variantData = this.fetchVariantData();
      this.productHandle = this.container.dataset.productHandle;
      await whenNearViewport(this.pcard || this);
      this.productData = await this.fetchProductJson();
      this.activeOptionNodeByPosition = {};
      this.hideUnavailableProductOptions = MinimogSettings.hide_unavailable_product_options;

      const { variantIdNode, productData, productData: { variants } = {} } = this;

      if (productData) {
        let currentVariantId = variantIdNode && Number(variantIdNode.value);
        if (!currentVariantId) {
          currentVariantId =
            productData.selected_or_first_available_variant && productData.selected_or_first_available_variant.id;
        }
        let currentVariant = variants.find((v) => v.id === currentVariantId) || variants[0];
        let switchedColor = false;

        // Com filtro de tamanho ativo na colecao, se a cor inicial nao tem estoque
        // nos tamanhos filtrados, o card abre na primeira cor que tem, em vez de
        // abrir esgotado com outra cor disponivel ao lado.
        const filteredSizes = this.getFilteredSizes();
        const sizePosition = this.getSizePosition();
        const colorNodes = this.getCardOptionNodes();
        const colorPosition = colorNodes.length ? Number(colorNodes[0].dataset.optionPosition) - 1 : -1;
        if (
          !this.keepFeaturedImage &&
          currentVariant &&
          filteredSizes.length &&
          sizePosition >= 0 &&
          colorPosition >= 0 &&
          colorPosition !== sizePosition
        ) {
          const availableInFilter = (variant) =>
            variant.available && filteredSizes.includes(variant.options[sizePosition]);
          const currentColor = currentVariant.options[colorPosition];
          const currentColorOk = variants.some(
            (v) => v.options[colorPosition] === currentColor && availableInFilter(v)
          );
          if (!currentColorOk) {
            const better = colorNodes
              .map((node) => this.pickVariantForValue(colorPosition, node.dataset.value))
              .find((v) => v && availableInFilter(v));
            if (better) {
              currentVariant = better;
              switchedColor = true;
            }
          }
        }

        // A bolinha marcada tem de ser a cor da foto que o card mostra. Sem isto, o
        // card abria na primeira cor com estoque (azul) mostrando a foto principal
        // de outra (bordo), porque a foto da cor marcada nao existe.
        if (!this.keepFeaturedImage && currentVariant && colorPosition >= 0 && colorPosition !== sizePosition) {
          const photoVariant = this.pickVariantMatchingPhoto(currentVariant, colorPosition, (variant) =>
            filteredSizes.length && sizePosition >= 0
              ? variant.available && filteredSizes.includes(variant.options[sizePosition])
              : variant.available
          );
          if (photoVariant && photoVariant !== currentVariant) {
            currentVariant = photoVariant;
            switchedColor = true;
          }
        }

        this.productData.initialVariant = currentVariant;
        if (!this.productData.selected_variant && variantIdNode && variantIdNode.dataset.selectedVariant) {
          this.productData.selected_variant = variants.find(
            (v) => v.id === Number(variantIdNode.dataset.selectedVariant)
          );
        }

        if (!this.keepFeaturedImage) {
          this.updateOptionsByVariant(currentVariant);
          this.updateProductImage(currentVariant);
          // Reaplica a foto quando o tema avisa que uma imagem carregou, mas sempre a
          // da variante atual: capturar a inicial fazia a foto voltar para a cor
          // anterior logo depois do clique, porque trocar a foto dispara este evento.
          window.MinimogEvents.subscribe("m:image-loaded", () => {
            this.updateProductImage(this.imageVariant || currentVariant);
          });
        }

        if (switchedColor && variantIdNode) {
          variantIdNode.setAttribute("value", String(currentVariant.id));
          variantIdNode.value = String(currentVariant.id);
          const productCard9 = this.closest("product-card-9");
          if (productCard9 && typeof productCard9.syncVariantFromCardSwatch === "function") {
            productCard9.syncVariantFromCardSwatch();
          }
        }
      }

      if (this.domNodes.optionNodes) {
        this.domNodes.optionNodes.forEach((node) =>
          node.addEventListener("click", this.handleVariantSelection.bind(this))
        );
      }
      if (this.domNodes.variantDropdown) {
        this.domNodes.variantDropdown.addEventListener("change", this.handleVariantSelection.bind(this));
      }
    }

    fetchVariantData() {
      if (!this.variantData) {
        var jsonElement = this.container && this.container.querySelector('[type="application/json"]');
        this.variantData = JSON.parse(jsonElement ? jsonElement.textContent : "{}");
      }
      return this.variantData;
    }

    fetchProductJson() {
      var productUrl = `${window.MinimogSettings.routes.root}/products/${this.productHandle}.js`;
      productUrl = productUrl.replace("//", "/");

      if (!productJsonRequests.has(productUrl)) {
        productJsonRequests.set(
          productUrl,
          fetchJsonWithRetry(productUrl)
            .catch(function (error) {
              // Nao deixa uma falha de rede envenenar o cache: o proximo card tenta de novo.
              productJsonRequests.delete(productUrl);
              throw error;
            })
        );
      }

      // Copia rasa por card: initialVariant e selected_variant sao gravados neste
      // objeto, e dois cards do mesmo produto nao podem escrever um sobre o outro.
      // variants e images seguem compartilhados, sao so lidos.
      return productJsonRequests.get(productUrl).then(function (data) {
        return Object.assign({}, data);
      });
    }

    toggleOptionNodeActive(optNode, active) {
      if (!optNode) return;
      if (active) {
        const { optionPosition, value: optionValue } = optNode.dataset;
        this.activeOptionNodeByPosition[optionPosition] = optNode;

        switch (optNode.tagName) {
          case "INPUT":
            optNode.checked = true;
            optNode.dataset.selected = "true";
            break;
          case "OPTION":
            optNode.dataset.selected = "true";
            const select = optNode.closest("select");
            if (select) select.value = optNode.value;
            break;
          case "LABEL":
            optNode.dataset.selected = "true";
            break;
          default:
            if (!optNode.classList.contains("m-product-quickview-button")) {
              console.warn("Unable to activate option node", optNode);
            }
            break;
        }

        this.updateSoldOutBadge(this.getVariantsByOptionValue(optionValue));
      } else {
        if (!["default", "image", "color"].includes(optNode.dataset.optionType)) {
          optNode.style.border = "";
        }
        optNode.checked = false;
        delete optNode.dataset.selected;
        const select = optNode.closest("select");
        if (select) select.value = "";
      }
    }

    updateOptionsByVariant(variant) {
      Object.values(this.activeOptionNodeByPosition).forEach((optNode) => this.toggleOptionNodeActive(optNode, false));
      // Limpa tambem a selecao que veio do render em Liquid (data-selected), para
      // a troca de cor na carga nao deixar duas bolinhas marcadas.
      this.getCardOptionNodes()
        .filter((node) => node.dataset.selected === "true")
        .forEach((node) => this.toggleOptionNodeActive(node, false));

      const { optionNodes } = this.domNodes;
      const { options = [] } = variant || {};
      options.forEach((option, index) => {
        const optPosition = index + 1;
        optionNodes.forEach((optNode) => {
          const _optPosition = Number(optNode.dataset.optionPosition);
          const _optValue = optNode.dataset.value;

          if (_optPosition === optPosition && option === _optValue) {
            this.toggleOptionNodeActive(optNode, true);
          }
        });
      });
      this.updatePrice(variant);
    }

    getVariantFromActiveOptions = () => {
      const {
        productData,
        productData: { initialVariant },
        activeOptionNodeByPosition,
      } = this;
      const initialVariantOptions = {
        1: initialVariant.option1,
        2: initialVariant.option2,
        3: initialVariant.option3,
      };

      Object.values(activeOptionNodeByPosition).forEach((optNode) => {
        const { optionPosition, value } = optNode.dataset;
        initialVariantOptions[optionPosition] = value;
      });

      var options = Object.values(initialVariantOptions).filter(Boolean);
      var variant = getVariantFromOptionArray(productData, options);
      if (!variant && this.hideUnavailableProductOptions) {
        options.pop();
        variant = getVariantFromOptionArray(productData, options);
        if (!variant) {
          options.pop();
          variant = getVariantFromOptionArray(productData, options);
        }
      }
      this.currentVariant = variant;
      return variant;
    };

    handleVariantSelection(e) {
      var target = e.target;
      var newVariant;

      if (target.classList.contains("combined-variant")) {
        const variantId = Number(e.target.value);
        newVariant =
          this.productData && this.productData.variants && this.productData.variants.find((v) => v.id === variantId);
      } else {
        if (target.tagName === "SELECT") {
          target = target.querySelectorAll("option")[target.selectedIndex];
        }
        if (!target.classList.contains("m-product-option--node__label")) {
          target = target.closest(".m-product-option--node__label");
          if (!target) console.error("Unable to find option node!");
        }
        const { optionPosition, value } = target.dataset;

        const currActiveOptNode = this.activeOptionNodeByPosition[optionPosition];
        this.toggleOptionNodeActive(currActiveOptNode, false);
        this.toggleOptionNodeActive(target, true);
        const pos = Number(optionPosition) - 1;
        const sizePos = this.getSizePosition();
        const sizeRendered = this.getCardOptionNodes().some(
          (node) => Number(node.dataset.optionPosition) - 1 === sizePos
        );

        if (sizePos >= 0 && pos !== sizePos && !sizeRendered) {
          // O card so mostra a cor. Sem tamanho escolhido pela cliente, a variante
          // nao pode ancorar no tamanho da variante corrente (M/PRETO esgotado
          // enquanto P, G e GG tinham estoque). Escolhe uma disponivel da cor em
          // qualquer tamanho, ou, com filtro de tamanho ativo na colecao, dentro
          // dos tamanhos filtrados.
          newVariant = this.pickVariantForValue(pos, value);
        } else {
          newVariant = this.getVariantFromActiveOptions();
        }

        // Fallback: se não encontrou, ou encontrou variante com a cor ERRADA
        // (hideUnavailableOptions pode fazer pop() na cor e retornar variante errada)
        if (!newVariant || newVariant.options[pos] !== value) {
          const variants = Array.isArray(this.variantData) ? this.variantData : [];
          newVariant = variants.find((v) => v.options[pos] === value && v.available)
            || variants.find((v) => v.options[pos] === value);
        }
      }

      if (!newVariant) return;

      const { variantIdNode } = this;
      if (variantIdNode) {
        variantIdNode.setAttribute("value", String(newVariant.id));
        variantIdNode.value = String(newVariant.id);
      }

      this.updateBySelectedVariant(newVariant);
    }

    updateBySelectedVariant(variant) {
      if (variant) {
        this.updateProductImage(variant);
        this.updatePrice(variant);
      }
    }

    updateProductCardSoldOutBadge(variant) {
      if (this.domNodes.soldOutBadge) {
        this.domNodes.soldOutBadge.style.display = variant.available ? "none" : "flex";
      }
    }

    updateSoldOutBadge(variants) {
      const soldOut = !variants.some((variant) => variant.available);
      if (this.domNodes.soldOutBadge) {
        this.domNodes.soldOutBadge.style.display = soldOut ? "flex" : "none";
      }
    }

    getVariantsByOptionValue = (value) => {
      const { productData: { variants } = {} } = this;
      const sizePos = this.getSizePosition();
      const sizes = this.getFilteredSizes();
      return variants.filter((variant) => {
        if (!variant.options.includes(value)) return false;
        if (sizePos < 0 || !sizes.length) return true;
        return sizes.includes(variant.options[sizePos]);
      });
    };

    // Labels de opcao do proprio swatch do card. O .m-product-card tambem contem
    // os labels do overlay de quick-add, que nao carregam data-option-position.
    getCardOptionNodes = () => {
      const scope = this.container || this;
      return Array.from(scope.querySelectorAll(".m-product-option--node__label")).filter(
        (node) => node.dataset.optionPosition && node.dataset.value
      );
    };

    // Posicao (0-based) da opcao de tamanho, exposta no wrapper pelo Liquid.
    // Cai para o nome da opcao no JSON do produto quando o atributo nao existe.
    getSizePosition = () => {
      const fromDom = Number(this.container && this.container.dataset.sizePosition);
      if (fromDom > 0) return fromDom - 1;
      const names = (this.productData && this.productData.options) || [];
      return names.findIndex((name) => /^(size|tamanho)$/i.test(String(name).trim()));
    };

    // Tamanhos filtrados na colecao: query string (fonte principal, sobrevive ao
    // AJAX dos facets) mais os checkboxes marcados no DOM. Vazio na home ou sem filtro.
    getFilteredSizes = () => {
      const param = (this.container && this.container.dataset.sizeParam) || "";
      if (!param) return [];
      const fromUrl = new URLSearchParams(window.location.search).getAll(param);
      const fromDom = Array.from(document.querySelectorAll(`input[name="${param}"]:checked`)).map(
        (input) => input.value
      );
      return Array.from(new Set(fromUrl.concat(fromDom)));
    };

    pickVariantForValue = (pos, value) => {
      const variants =
        Array.isArray(this.variantData) && this.variantData.length
          ? this.variantData
          : (this.productData && this.productData.variants) || [];
      const candidates = variants.filter((variant) => variant.options[pos] === value);
      if (!candidates.length) return null;

      const sizePos = this.getSizePosition();
      const sizes = this.getFilteredSizes();
      if (sizePos >= 0 && sizePos !== pos && sizes.length) {
        const inSizes = candidates.filter((variant) => sizes.includes(variant.options[sizePos]));
        return (
          inSizes.find((variant) => variant.available) ||
          inSizes[0] ||
          candidates.find((variant) => !variant.available) ||
          candidates[0]
        );
      }
      return candidates.find((variant) => variant.available) || candidates[0];
    };

    // Fotos da cor pelo agrupamento de imagens por variante do FoxKit (metafield
    // foxecom-bs-kit.variant-images), publicado no card pelo product-card-option.
    // E a mesma fonte que a PDP usa para filtrar a galeria: sem ela o card nao tem
    // como saber qual foto e de qual cor, porque variant.featured_image e sempre nulo.
    // Os ids de midia viram URL pelo product.js que o card ja busca.
    getVariantGroupMapping() {
      if (this.variantGroupMapping === undefined) {
        const node = this.container && this.container.querySelector("[data-pcard-variant-group]");
        try {
          this.variantGroupMapping = node ? JSON.parse(node.textContent) : null;
        } catch (error) {
          this.variantGroupMapping = null;
        }
      }
      return Array.isArray(this.variantGroupMapping) ? this.variantGroupMapping : null;
    }

    // Variante com estoque cuja cor e a da foto mostrada na carga, ou null para
    // manter a atual. Com agrupamento, a foto mostrada e a da cor marcada; so muda
    // quando essa cor nao tem foto. Sem agrupamento, a foto mostrada e a principal
    // do produto, e a cor dela sai do nome do arquivo (colorFromFileName).
    pickVariantMatchingPhoto(currentVariant, colorPosition, isEligible) {
      const variants = (this.productData && this.productData.variants) || [];
      const media = (this.productData && this.productData.media) || [];
      const colors = this.getCardOptionNodes()
        .filter((node) => Number(node.dataset.optionPosition) - 1 === colorPosition)
        .map((node) => node.dataset.value);
      const currentColor = currentVariant.options[colorPosition];
      const firstEligible = (color) =>
        variants.find((variant) => variant.options[colorPosition] === color && isEligible(variant)) || null;

      const mapping = this.getVariantGroupMapping();
      if (mapping) {
        const colorsWithPhoto = colors.filter((color) =>
          variants.some(
            (variant) => variant.options[colorPosition] === color && this.getVariantGroupImages(variant).length
          )
        );
        if (!colorsWithPhoto.length || colorsWithPhoto.includes(currentColor)) return null;

        // Preferencia: a cor cujo grupo contem a foto principal do produto.
        const featuredId = media[0] && String(media[0].id);
        const featuredColor = colorsWithPhoto.find((color) =>
          mapping.some((item) => {
            const variant = variants.find((v) => Number(v.id) === Number(item.id));
            return variant && variant.options[colorPosition] === color && (item.media || []).map(String).includes(featuredId);
          })
        );
        const ordered = featuredColor ? [featuredColor].concat(colorsWithPhoto) : colorsWithPhoto;
        for (const color of ordered) {
          const variant = firstEligible(color);
          if (variant) return variant;
        }
        return null;
      }

      // Sem agrupamento: a foto principal; se o nome dela nao diz a cor, vale a
      // unica cor que as fotos do produto mostram.
      const codes = colorCodesByValue(variants, colorPosition);
      let photoColor = media[0] ? colorFromFileName(media[0].src, colors, codes) : null;
      if (!photoColor) {
        const found = new Set(media.map((item) => colorFromFileName(item.src, colors, codes)).filter(Boolean));
        if (found.size === 1) photoColor = Array.from(found)[0];
      }
      if (!photoColor || photoColor === currentColor) return null;
      return firstEligible(photoColor);
    }

    getVariantGroupImages(variant) {
      if (!variant) return [];

      const mapping = this.getVariantGroupMapping();
      if (!mapping) return [];

      const group = mapping.find((item) => Number(item.id) === Number(variant.id));
      const mediaIds = (group && group.media) || [];
      if (!mediaIds.length) return [];

      const media = (this.productData && this.productData.media) || [];
      return mediaIds
        .map((id) => media.find((item) => String(item.id) === String(id)))
        .filter((item) => item && item.media_type === "image" && item.src)
        .map((item) => item.src);
    }

    updateProductImage(variant) {
      const { featuredImage, pcard, productData } = this;
      // Guarda a variante que a foto deve mostrar, para o m:image-loaded reaplicar
      // esta, e nao a do carregamento da pagina.
      if (variant) this.imageVariant = variant;
      const featImg = variant && variant.featured_image;
      let mainSrc = featImg && featImg.src;
      let hoverSrc = null;

      if (mainSrc) {
        // Imagem nativa da variante: o hover e a foto seguinte do produto.
        const images = (productData && productData.images) || [];
        const pos = featImg.position; // 1-based
        if (pos > 0 && pos < images.length) hoverSrc = images[pos];
      } else {
        // Sem imagem nativa, cai no agrupamento: 1a foto da cor no card, 2a no hover.
        const groupImages = this.getVariantGroupImages(variant);
        mainSrc = groupImages[0] || null;
        hoverSrc = groupImages[1] || null;
      }

      setCardImage(featuredImage, mainSrc);
      setCardImage(pcard && pcard.querySelector(".m-product-card__hover-image"), hoverSrc);
    }

    updatePrice(variant) {
      if (MinimogSettings.pcard_show_lowest_prices) return;
      const classes = {
        onSale: "m-price--on-sale",
        soldOut: "m-price--sold-out",
      };

      const moneyFormat = window.MinimogSettings.money_format;

      const { priceWrapper, salePrice, unitPrice, compareAtPrice } = this.domNodes;
      const { compare_at_price, price, unit_price_measurement } = variant;
      const onSale = compare_at_price && compare_at_price > price;
      const soldOut = !variant.available;

      if (onSale) {
        priceWrapper && priceWrapper.classList.add(classes.onSale);
      } else {
        priceWrapper && priceWrapper.classList.remove(classes.onSale);
      }

      if (soldOut) {
        priceWrapper && priceWrapper.classList.add(classes.soldOut);
      } else {
        priceWrapper && priceWrapper.classList.remove(classes.soldOut);
      }

      priceWrapper && priceWrapper.classList.remove("visibility-hidden");
      const displayPrice = price;
      if (salePrice) salePrice.innerHTML = formatMoney(displayPrice, moneyFormat);

      if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
        compareAtPrice.forEach((item) => (item.innerHTML = formatMoney(compare_at_price, moneyFormat)));
      } else {
        compareAtPrice.forEach((item) => (item.innerHTML = formatMoney(displayPrice, moneyFormat)));
      }

      if (unit_price_measurement && unitPrice && this.currentVariant) {
        unitPrice.classList.remove("f-hidden");
        const unitPriceContent = `<span>${formatMoney(
          this.currentVariant.unit_price,
          moneyFormat
        )}</span>/<span data-unit-price-base-unit>${this.getBaseUnit()}</span>`;
        unitPrice.innerHTML = unitPriceContent;
      } else {
        unitPrice && unitPrice.classList.add("f-hidden");
      }
    }

    getBaseUnit = () => {
      return this.currentVariant.unit_price_measurement.reference_value === 1
        ? this.currentVariant.unit_price_measurement.reference_unit
        : this.currentVariant.unit_price_measurement.reference_value +
        this.currentVariant.unit_price_measurement.reference_unit;
    };
  }

  customElements.define("pcard-swatch", ProductCardColorSwatch);
}